import React, { useEffect } from 'react';
import { Box } from '@mui/material';

/** Height of the strip, in pixels. Also the offset everything else needs. */
const BANNER_HEIGHT = 28;

/**
 * A fixed strip shown when the locally-served UI is talking to a DEPLOYED
 * backend (`npm run dev:prod`).
 *
 * Reason: the whole point of that mode is that the app looks and behaves
 * exactly as it does in production -- which means nothing on screen would
 * otherwise distinguish "my local build reading real data" from "the real
 * site". Deleting an account in that state deletes it for real. The banner is
 * deliberately hard to ignore and deliberately impossible to ship: the label
 * comes from an env var that only the dev:* scripts set, and Vite inlines it
 * as undefined in a production build, so this renders nothing there.
 */
export const RemoteEnvBanner: React.FC = () => {
  const label = import.meta.env.VITE_ENV_LABEL as string | undefined;

  // Publish the height as a CSS variable so the fixed AppBar and Drawer can
  // offset themselves by it.
  //
  // The strip has to be fixed -- it must stay visible while scrolling, since
  // its whole job is to stop you forgetting which data you are editing. But
  // fixed elements are outside normal flow, so it was drawing ON TOP of the
  // logo and nav. Reserving the space through a variable keeps it pinned while
  // still pushing the app down, and resolves to 0px in a production build
  // where the banner does not render at all.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--env-banner-h', label ? `${BANNER_HEIGHT}px` : '0px');
    return () => root.style.setProperty('--env-banner-h', '0px');
  }, [label]);

  if (!label) return null;

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        // Above the AppBar (1100) and the Drawer, or the thing warning you
        // about production would itself be hidden behind the chrome.
        zIndex: 2000,
        height: BANNER_HEIGHT,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        textAlign: 'center',
        fontSize: '0.75rem',
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: '#1a1a1a',
        // Amber rather than red: this is a caution, not an error state, and a
        // red bar across a finance dashboard reads as "something is broken".
        background: label === 'production' ? '#ffb020' : '#7fd4c1',
      }}
    >
      Local UI · {label} data · writes are real
    </Box>
  );
};
